import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  Building2,
  Plus,
  Loader2,
  Briefcase,
  Layers,
  Upload,
  MapPin,
  Check,
  ArrowLeft,
} from 'lucide-react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'

import { AppShell } from '@/admin/components/app-shell'
import { ConfigHelp } from '@/admin/components/config-help'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Badge } from '@/shared/components/ui/badge'
import { SearchInput } from '@/shared/components/ui/search-input'
import {
  ProductFilterMenu,
  type ProductFilterFacet,
  type ProductFilterValue,
} from '@/shared/components/ui/product-filter-menu'
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value'
import { useTableFilterPreferences } from '@/admin/hooks/use-table-filter-preferences'
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/shared/components/ui/tabs'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { useWarrantyTemplates } from '@/admin/hooks/use-warranties'
import { useClaims, type ClaimRow } from '@/admin/hooks/use-claims'
import {
  BulkFileClaimsDialog,
  type BulkClaimUnit,
} from '@/admin/components/claims/bulk-file-claims-dialog'
import { BulkImportDialog } from '@/admin/components/imports/bulk-import-dialog'
import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import { parseHoaFile } from '@/admin/lib/hoa-csv'
import api from '@/admin/lib/api'
import { FileDropzone } from '@/shared/components/ui/file-dropzone'
import {
  useHoaTree,
  useCreateDeveloper,
  useCreateBuilding,
  useCreateUnit,
  useBulkRegisterUnits,
  useRegisterUnit,
  useSetBuildingWarranty,
  type HoaCommunityNode,
  type HoaBuildingNode,
  type HoaUnitNode,
  type BulkRegisterRow,
} from '@/admin/hooks/use-hoa-communities'
import { toastApiError } from '@/shared/lib/api-error'
import { useFormatters } from '@/shared/lib/formatting'

/** Single-field quick-add dialog (developer / building). */
function QuickAddDialog({
  open,
  onOpenChange,
  title,
  description,
  fieldLabel,
  placeholder,
  pending,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: string
  fieldLabel: string
  placeholder: string
  pending: boolean
  onSubmit: (value: string) => Promise<void>
}) {
  const [value, setValue] = useState('')
  const submit = async () => {
    if (!value.trim()) return
    await onSubmit(value.trim())
    setValue('')
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setValue('')
        onOpenChange(o)
      }}
    >
      <DialogContent className="max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
            {title}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {fieldLabel}
          </Label>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit()
            }}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="cursor-pointer">
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending || !value.trim()} className="cursor-pointer gap-1.5">
            {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Add a single unit with product / colour / contractor. */
function UnitDialog({
  open,
  onOpenChange,
  pending,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  pending: boolean
  onSubmit: (v: { label: string; product: string; color: string; contractor: string }) => Promise<void>
}) {
  const [label, setLabel] = useState('')
  const [product, setProduct] = useState('')
  const [color, setColor] = useState('')
  const [contractor, setContractor] = useState('')
  const reset = () => {
    setLabel('')
    setProduct('')
    setColor('')
    setContractor('')
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset()
        onOpenChange(o)
      }}
    >
      <DialogContent className="max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">Add Unit</DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            An individual unit (e.g. Apartment 1203) with its product details.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 flex flex-col gap-3">
          <div>
            <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">Unit / Apartment / Home No</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Apartment 1203" autoFocus />
          </div>
          <div>
            <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">Product</Label>
            <Input value={product} onChange={(e) => setProduct(e.target.value)} placeholder="e.g. TruDefinition Duration" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">Colour</Label>
              <Input value={color} onChange={(e) => setColor(e.target.value)} placeholder="e.g. Estate Gray" />
            </div>
            <div>
              <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">Contractor</Label>
              <Input value={contractor} onChange={(e) => setContractor(e.target.value)} placeholder="e.g. Ace Roofing" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="cursor-pointer">Cancel</Button>
          <Button
            onClick={async () => {
              if (!label.trim()) return
              await onSubmit({ label: label.trim(), product, color, contractor })
              reset()
            }}
            disabled={pending || !label.trim()}
            className="cursor-pointer gap-1.5"
          >
            {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Add Unit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Pick a default warranty for a building - its units inherit it. */
function BuildingWarrantyDialog({
  building,
  onOpenChange,
  pending,
  onSubmit,
}: {
  building: HoaBuildingNode | null
  onOpenChange: (open: boolean) => void
  pending: boolean
  onSubmit: (optionUuid: string) => Promise<void>
}) {
  const [optionUuid, setOptionUuid] = useState('')
  const { data: templates = [] } = useWarrantyTemplates(undefined, {
    enabled: !!building,
  })
  return (
    <Dialog
      open={!!building}
      onOpenChange={(o) => {
        if (!o) setOptionUuid('')
        onOpenChange(o)
      }}
    >
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
            Default Warranty - {building?.name}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            Every unit registered under this building inherits this warranty (you
            can override per unit at registration).
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
            Warranty
          </Label>
          <Select value={optionUuid} onValueChange={setOptionUuid}>
            <SelectTrigger>
              <SelectValue placeholder="Select a warranty" />
            </SelectTrigger>
            <SelectContent>
              {templates.flatMap((tmpl) =>
                (tmpl.options ?? []).map((opt) => (
                  <SelectItem key={opt.uuid} value={opt.uuid}>
                    {opt.display_name.startsWith(tmpl.name)
                      ? opt.display_name
                      : `${tmpl.name} - ${opt.display_name}`}
                  </SelectItem>
                )),
              )}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!optionUuid) return
              await onSubmit(optionUuid)
              setOptionUuid('')
            }}
            disabled={pending || !optionUuid}
            className="cursor-pointer gap-1.5"
          >
            {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Set Warranty
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Register a unit as a real warranty registration (capture the homeowner). */
function RegisterUnitDialog({
  unit,
  onOpenChange,
  pending,
  onSubmit,
}: {
  unit: HoaUnitNode | null
  onOpenChange: (open: boolean) => void
  pending: boolean
  onSubmit: (v: {
    homeowner_email: string
    homeowner_first_name: string
    homeowner_last_name: string
    correspondence_address: {
      line1: string
      city: string
      state: string
      zip_code: string
    }
  }) => Promise<void>
}) {
  const [email, setEmail] = useState('')
  const [ownerName, setOwnerName] = useState('')
  const [addr1, setAddr1] = useState('')
  const [city, setCity] = useState('')
  const [stateRegion, setStateRegion] = useState('')
  const [zip, setZip] = useState('')
  const reset = () => {
    setEmail('')
    setOwnerName('')
    setAddr1('')
    setCity('')
    setStateRegion('')
    setZip('')
  }
  return (
    <Dialog
      open={!!unit}
      onOpenChange={(o) => {
        if (!o) reset()
        onOpenChange(o)
      }}
    >
      <DialogContent className="max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
            Register {unit?.name}
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            Create this unit&apos;s warranty registration so it can be claimed.
            Product{unit?.product ? ` (${unit.product})` : ''} and colour come
            from the unit; just add the homeowner.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 flex flex-col gap-3">
          <div>
            <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
              Homeowner Email
            </Label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="homeowner@email.com"
              autoFocus
            />
          </div>
          <div>
            <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
              Owner Full Name
            </Label>
            <Input
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              placeholder="e.g. Jane Doe"
            />
          </div>
          <div>
            <Label className="mb-1.5 block text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
              Correspondence Address
            </Label>
            <Input
              value={addr1}
              onChange={(e) => setAddr1(e.target.value)}
              placeholder="Street address"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="City"
            />
            <Input
              value={stateRegion}
              onChange={(e) => setStateRegion(e.target.value)}
              placeholder="State"
            />
            <Input
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              placeholder="ZIP / Postcode"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!email.trim()) return
              const parts = ownerName.trim().split(/\s+/).filter(Boolean)
              await onSubmit({
                homeowner_email: email.trim(),
                homeowner_first_name: parts[0] ?? '',
                homeowner_last_name: parts.slice(1).join(' '),
                correspondence_address: {
                  line1: addr1.trim(),
                  city: city.trim(),
                  state: stateRegion.trim(),
                  zip_code: zip.trim(),
                },
              })
              reset()
            }}
            disabled={pending || !email.trim()}
            className="cursor-pointer gap-1.5"
          >
            {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Register Unit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Bulk-upload units from a CSV file (header-mapped) and register owners. */
function BulkUnitsDialog({
  open,
  onOpenChange,
  pending,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  pending: boolean
  onSubmit: (rows: BulkRegisterRow[]) => Promise<void>
}) {
  const [fileName, setFileName] = useState('')
  const [rows, setRows] = useState<BulkRegisterRow[]>([])
  const [matched, setMatched] = useState<(keyof BulkRegisterRow)[]>([])
  const [hasLabel, setHasLabel] = useState(true)
  const [error, setError] = useState('')

  const reset = () => {
    setFileName('')
    setRows([])
    setMatched([])
    setHasLabel(true)
    setError('')
  }

  const handleFile = async (file: File) => {
    setError('')
    setFileName(file.name)
    try {
      const parsed = await parseHoaFile(file)
      setRows(parsed.rows)
      setMatched(parsed.matchedColumns)
      setHasLabel(parsed.hasLabel)
      if (!parsed.hasLabel) {
        setError(
          'No "Unit / Apartment / Home No" column found. Add a header row with that column.',
        )
      } else if (parsed.rows.length === 0) {
        setError('No unit rows found below the header.')
      }
    } catch {
      setError('Could not read that file. Use a .csv or .xlsx file.')
      setRows([])
    }
  }

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/registrations/hoa/register-template/', {
        responseType: 'blob',
      })
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'community-registration-template.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      toastApiError(err, 'Could not download the template')
    }
  }

  const withOwners = rows.filter((r) => r.owner_email).length

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset()
        onOpenChange(o)
      }}
    >
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
            Bulk Upload &amp; Register Units
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            Upload a CSV or Excel file with a header row. Columns are matched by
            name - e.g.{' '}
            <span className="font-mono">
              Unit / Apartment / Home No, Product, Colour, Contractor, Warranty,
              Owner Name, Owner Email, Address, City, State, ZIP
            </span>
            . Rows with an owner email are registered to that owner.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 flex flex-col gap-3">
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={downloadTemplate}
              className="cursor-pointer gap-1 text-[var(--ink-3)] hover:text-[var(--ink)]"
            >
              <Upload size={13} className="rotate-180" />
              Download template
            </Button>
          </div>
          <FileDropzone
            accept=".csv,.xlsx,.xls,text/csv"
            onFile={(f) => void handleFile(f)}
            hint="CSV or Excel - units, owners, products, warranties"
          />
          {fileName && (
            <p className="text-[11px] text-[var(--ink-3)]">Loaded: {fileName}</p>
          )}

          {error && (
            <p className="text-[12px] text-[var(--status-error)]">{error}</p>
          )}

          {rows.length > 0 && !error && (
            <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2">
              <p className="text-[12px] text-[var(--ink)]">
                {rows.length} unit{rows.length !== 1 ? 's' : ''} detected ·{' '}
                {withOwners} with owner (will register)
              </p>
              <p className="mt-1 text-[11px] text-[var(--ink-3)]">
                Matched columns: {matched.join(', ') || 'none'}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (rows.length === 0 || !hasLabel) return
              await onSubmit(rows)
              reset()
            }}
            disabled={pending || rows.length === 0 || !hasLabel}
            className="cursor-pointer gap-1.5"
          >
            {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Add {rows.length} unit{rows.length !== 1 ? 's' : ''}
            {withOwners > 0 ? ` (${withOwners} registered)` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** A single community flattened for the overview table + drill-down. */
interface CommunityRow {
  uuid: string
  name: string
  developer: string
  address: string
  buildings: number
  units: number
  registered: number
  holdingUuids: string[]
  createdAt: string
  node: HoaCommunityNode
}

/** A single unit flattened for the HOA registrations table. */
interface UnitRow {
  uuid: string
  unit: string
  community: string
  building: string
  product: string
  color: string
  registered: boolean
  holding_uuid?: string
  registrationNumber: string
  warrantyType: string
}

/** A claim tied back to its HOA community. */
type HoaClaimRow = ClaimRow & { community: string; communityUuid: string }

/** Flatten a community node into its overview-row shape. */
function flattenCommunity(node: HoaCommunityNode, developer: string): CommunityRow {
  let units = 0
  let registered = 0
  const holdingUuids: string[] = []
  for (const b of node.buildings) {
    units += b.units.length
    for (const u of b.units) {
      if (u.registered) registered += 1
      if (u.holding_uuid) holdingUuids.push(u.holding_uuid)
    }
  }
  return {
    uuid: node.uuid,
    name: node.name,
    developer,
    address: node.address || '',
    buildings: node.buildings.length,
    units,
    registered,
    holdingUuids,
    createdAt: node.created_at || '',
    node,
  }
}

/** Every community's claims (the Claims tab). */
const ALL_COMMUNITIES = { host_role: 'community' }

/** Comma-joined Filters panel picks, as list query params. */
function filterParams(values: ProductFilterValue): Record<string, string> {
  const p: Record<string, string> = {}
  for (const [key, picks] of Object.entries(values)) {
    if (picks.length) p[key] = picks.join(',')
  }
  return p
}

/**
 * HOA claims, one server page at a time, with search and the claims list's
 * own Filters panel. ``scope`` narrows to every community or to one.
 */
function HoaClaimsTable({
  tableId,
  scope,
  columns,
  communityByHolding,
  emptyMessage,
  onRowClick,
}: {
  tableId: string
  scope: Record<string, string>
  columns: DataTableColumn<HoaClaimRow>[]
  communityByHolding: Record<string, CommunityRow>
  emptyMessage: string
  onRowClick: (row: HoaClaimRow) => void
}) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search)
  const [filterValues, setFilterValues] = useTableFilterPreferences(tableId)
  const params = {
    ...scope,
    ...filterParams(filterValues),
    ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
    page: String(page),
    page_size: String(pageSize),
  }
  const { data, isLoading, isFetching, isError, refetch } = useClaims(params, {
    keepPreviousPage: true,
  })
  const rows = (data?.results ?? []).map((cl) => {
    const community = communityByHolding[cl.holding_uuid]
    return { ...cl, community: community?.name ?? '-', communityUuid: community?.uuid ?? '' }
  })
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end gap-2">
        <ProductFilterMenu
          facets={data?.filter_options ?? []}
          value={filterValues}
          onChange={(values) => {
            setFilterValues(values)
            setPage(1)
          }}
        />
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value)
            setPage(1)
          }}
          placeholder="Search claims..."
          className="w-[200px]"
        />
      </div>
      <DataTable
        tableId={tableId}
        columns={columns}
        data={rows}
        totalCount={data?.count ?? 0}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        onRetry={refetch}
        onRowClick={onRowClick}
        emptyMessage={emptyMessage}
      />
    </div>
  )
}

/** One select facet over the distinct values a column holds. */
function valuesFacet(
  key: string,
  label: string,
  values: string[],
  labelOf: (value: string) => string = (value) => value,
): ProductFilterFacet {
  return {
    key,
    label,
    type: 'select',
    kind: 'field',
    options: [...new Set(values.filter(Boolean))]
      .sort()
      .map((value) => ({ value, label: labelOf(value) })),
  }
}

/** Whether a row's value passes one facet (no pick passes everything). */
function picked(filters: ProductFilterValue, key: string, value: string): boolean {
  const picks = filters[key] ?? []
  return picks.length === 0 || picks.includes(value)
}

function communityMatches(r: CommunityRow, term: string, f: ProductFilterValue): boolean {
  return (
    picked(f, 'developer', r.developer) &&
    (!term || [r.name, r.developer, r.address].some((v) => v.toLowerCase().includes(term)))
  )
}

function unitMatches(r: UnitRow, term: string, f: ProductFilterValue): boolean {
  return (
    picked(f, 'community', r.community) &&
    picked(f, 'status', r.registered ? 'registered' : 'pending') &&
    picked(f, 'warranty_type', r.warrantyType) &&
    (!term ||
      [r.unit, r.community, r.building, r.registrationNumber, r.product].some((v) =>
        v.toLowerCase().includes(term),
      ))
  )
}

/**
 * A table built from the HOA tree the page already holds (the drill-in needs
 * all of it): search, the Filters panel and the pager run in the browser.
 */
function TreeTable<T>({
  tableId,
  rows,
  columns,
  facets,
  matches,
  searchPlaceholder,
  rowKey,
  onRowClick,
}: {
  tableId: string
  rows: T[]
  columns: DataTableColumn<T>[]
  facets: ProductFilterFacet[]
  matches: (row: T, term: string, filters: ProductFilterValue) => boolean
  searchPlaceholder: string
  rowKey: (row: T) => string
  onRowClick?: (row: T) => void
}) {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [search, setSearch] = useState('')
  const [filterValues, setFilterValues] = useTableFilterPreferences(tableId)
  const term = search.trim().toLowerCase()
  const shown = rows.filter((row) => matches(row, term, filterValues))
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-end gap-2">
        <ProductFilterMenu
          facets={facets.filter((f) => f.options.length > 0)}
          value={filterValues}
          onChange={(values) => {
            setFilterValues(values)
            setPage(1)
          }}
        />
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value)
            setPage(1)
          }}
          placeholder={searchPlaceholder}
          className="w-[200px]"
        />
      </div>
      <DataTable
        tableId={tableId}
        columns={columns}
        data={shown.slice((page - 1) * pageSize, page * pageSize)}
        totalCount={shown.length}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
        rowKey={rowKey}
        onRowClick={onRowClick}
      />
    </div>
  )
}

export function HoaCommunitiesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { formatDate } = useFormatters()
  const { data: tree = [], isLoading, refetch: refetchTree } = useHoaTree()
  const createDeveloper = useCreateDeveloper()
  const createBuilding = useCreateBuilding()
  const createUnit = useCreateUnit()
  const bulkUnits = useBulkRegisterUnits()
  const registerUnitMut = useRegisterUnit()
  const setBuildingWarranty = useSetBuildingWarranty()

  const [addDevOpen, setAddDevOpen] = useState(false)
  const [bulkCreateOpen, setBulkCreateOpen] = useState(false)
  const [buildingFor, setBuildingFor] = useState<string | null>(null)
  const [unitFor, setUnitFor] = useState<string | null>(null)
  const [bulkFor, setBulkFor] = useState<string | null>(null)
  const [registerUnit, setRegisterUnit] = useState<HoaUnitNode | null>(null)
  const [warrantyForBuilding, setWarrantyForBuilding] =
    useState<HoaBuildingNode | null>(null)

  // Home-base view state: which tab, and which community is drilled into.
  const [tab, setTab] = useState<'communities' | 'registrations' | 'claims'>(
    'communities',
  )
  const [selectedCommunityUuid, setSelectedCommunityUuid] = useState<
    string | null
  >(null)
  const [bulkClaimsOpen, setBulkClaimsOpen] = useState(false)
  const bulkImportsEnabled = useFeatureFlag(FLAGS.BULK_IMPORTS)
  const registrationGroupsEnabled = useFeatureFlag(FLAGS.REGISTRATION_GROUPS)
  const [bulkImportOpen, setBulkImportOpen] = useState(false)
  // Units ticked directly in the community grid - drives "Bulk File Claims".
  const [selectedClaimUnits, setSelectedClaimUnits] = useState<Set<string>>(
    () => new Set(),
  )
  const toggleClaimUnit = (uuid: string) =>
    setSelectedClaimUnits((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })
  const toggleAllClaimable = (claimable: { uuid: string }[]) =>
    setSelectedClaimUnits((prev) => {
      const allOn = claimable.length > 0 && claimable.every((u) => prev.has(u.uuid))
      const next = new Set(prev)
      if (allOn) claimable.forEach((u) => next.delete(u.uuid))
      else claimable.forEach((u) => next.add(u.uuid))
      return next
    })

  const hasContent = tree.length > 0

  // Flatten the developer → community → building → unit tree into the
  // per-community summaries and flat lists the data tabs render from.
  const communities = useMemo<CommunityRow[]>(() => {
    const out: CommunityRow[] = []
    for (const node of tree) {
      if (node.type === 'community') {
        out.push(flattenCommunity(node, '-'))
      } else {
        for (const c of node.communities) out.push(flattenCommunity(c, node.name))
      }
    }
    // Most recently created community first.
    out.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    return out
  }, [tree])

  const unitRows = useMemo<UnitRow[]>(() => {
    const out: UnitRow[] = []
    for (const c of communities) {
      for (const b of c.node.buildings) {
        for (const u of b.units) {
          out.push({
            uuid: u.uuid,
            unit: u.name,
            community: c.name,
            building: b.name,
            product: u.product || '',
            color: u.color || '',
            registered: !!u.registered,
            holding_uuid: u.holding_uuid,
            // Mirror the Registrations page: fall back to the holding's short
            // code when no explicit registration number is assigned yet.
            registrationNumber:
              u.registration_number ||
              (u.registered && u.holding_uuid ? u.holding_uuid.slice(0, 8) : ''),
            warrantyType: u.warranty_type || '',
          })
        }
      }
    }
    return out
  }, [communities])

  // A claim's community, by its holding (the claims list pages on the server;
  // the tree names the community each registered unit's holding sits in).
  const communityByHolding = useMemo<Record<string, CommunityRow>>(() => {
    const byHolding: Record<string, CommunityRow> = {}
    for (const c of communities) {
      for (const h of c.holdingUuids) byHolding[h] = c
    }
    return byHolding
  }, [communities])
  // Counted on the server, over the claims this viewer may see.
  const totalClaims = communities.reduce((n, c) => n + (c.node.claim_count ?? 0), 0)

  const selectedCommunity =
    communities.find((c) => c.uuid === selectedCommunityUuid) ?? null

  // Registered units of the drilled-in community - the pool for bulk claims.
  const bulkClaimUnits: BulkClaimUnit[] = selectedCommunity
    ? selectedCommunity.node.buildings.flatMap((b) =>
        b.units
          .filter((u) => u.registered && u.holding_uuid)
          .map((u) => ({
            uuid: u.uuid,
            name: u.name,
            holding_uuid: u.holding_uuid as string,
          })),
      )
    : []

  // If the user ticked specific units in the grid, file only those; otherwise
  // fall back to the whole community.
  const gridSelectedUnits = bulkClaimUnits.filter((u) =>
    selectedClaimUnits.has(u.uuid),
  )
  const claimDialogUnits =
    gridSelectedUnits.length > 0 ? gridSelectedUnits : bulkClaimUnits

  // ── Table column definitions ────────────────────────────────────────
  const communityColumns: DataTableColumn<CommunityRow>[] = [
    {
      id: 'name',
      header: 'Community',
      cell: (r) => (
        <span className="font-medium text-[var(--ink)]">{r.name}</span>
      ),
    },
    { id: 'developer', header: 'Developer', accessorKey: 'developer' },
    { id: 'address', header: 'Address', cell: (r) => r.address || '-' },
    { id: 'buildings', header: 'Buildings', cell: (r) => String(r.buildings) },
    { id: 'units', header: 'Units', cell: (r) => String(r.units) },
    {
      id: 'registered',
      header: 'Registered',
      cell: (r) => `${r.registered} / ${r.units}`,
    },
    {
      id: 'claims',
      header: 'Claims',
      cell: (r) => String(r.node.claim_count ?? 0),
    },
  ]

  const unitColumns: DataTableColumn<UnitRow>[] = [
    {
      id: 'unit',
      header: 'Unit / Apartment / Home No',
      cell: (r) => <span className="text-[var(--ink)]">{r.unit}</span>,
    },
    { id: 'community', header: 'Community', accessorKey: 'community' },
    { id: 'building', header: 'Building', accessorKey: 'building' },
    {
      id: 'reg_no',
      header: 'Warranty Reg No',
      cell: (r) =>
        r.registrationNumber ? (
          <span className="font-mono text-[11px] text-[var(--ink-2)]">
            {r.registrationNumber}
          </span>
        ) : (
          '-'
        ),
    },
    {
      id: 'warranty_type',
      header: 'Warranty Type',
      cell: (r) =>
        r.warrantyType ? (
          <Badge variant="outline">{r.warrantyType}</Badge>
        ) : (
          '-'
        ),
    },
    { id: 'product', header: 'Product', cell: (r) => r.product || '-' },
    { id: 'color', header: 'Colour', cell: (r) => r.color || '-' },
    {
      id: 'status',
      header: 'Status',
      cell: (r) =>
        r.registered ? (
          <Badge variant="secondary">Registered</Badge>
        ) : (
          <Badge variant="outline">Pending</Badge>
        ),
    },
    {
      // Placeholder - transfer-of-ownership wiring comes later.
      id: 'transfer',
      header: 'Transfer',
      cell: (r) =>
        r.registered ? (
          <Button
            variant="ghost"
            size="xs"
            disabled
            className="gap-1 text-[11px] text-[var(--ink-3)]"
            title="Transfer of ownership - coming soon"
          >
            Transfer
          </Button>
        ) : (
          '-'
        ),
    },
  ]

  const claimColumns: DataTableColumn<HoaClaimRow>[] = [
    { id: 'type', header: 'Type', accessorKey: 'claim_schema_name' },
    { id: 'community', header: 'Community', accessorKey: 'community' },
    { id: 'product', header: 'Product', accessorKey: 'product_name' },
    {
      id: 'owner',
      header: 'Owner',
      cell: (r) => r.consumer_account_name || '-',
    },
    {
      id: 'state',
      header: 'Status',
      cell: (r) =>
        r.claim_state ? (
          <Badge variant="secondary">{r.claim_state}</Badge>
        ) : (
          '-'
        ),
    },
    {
      id: 'created',
      header: 'Created',
      cell: (r) => formatDate(r.created_at),
    },
  ]

  const renderBuilding = (b: HoaBuildingNode) => {
    // Only registered units can be claimed - they drive the grid checkboxes.
    const claimableUnits = b.units.filter((u) => u.registered && u.holding_uuid)
    const allClaimableSelected =
      claimableUnits.length > 0 &&
      claimableUnits.every((u) => selectedClaimUnits.has(u.uuid))
    return (
    <div key={b.uuid} className="ml-5 mt-2 border-l-[0.5px] border-[var(--rule)] pl-4">
      <div className="flex items-center justify-between py-1">
        <div className="flex items-center gap-2">
          <Layers size={14} className="text-[var(--ink-2)]" />
          <span className="text-[13px] font-medium text-[var(--ink)]">{b.name}</span>
          <span className="text-[11px] text-[var(--ink-3)]">
            {b.units.length} unit{b.units.length !== 1 ? 's' : ''}
          </span>
          <span className="text-[11px] text-[var(--ink-3)]">
            {b.default_warranty_name
              ? `· Warranty: ${b.default_warranty_name}`
              : '· No default warranty'}
          </span>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" onClick={() => setWarrantyForBuilding(b)} className="cursor-pointer gap-1 text-[11px] uppercase tracking-[0.08em] text-[var(--ink-3)] hover:text-[var(--ink)]">
            <Plus size={12} />
            Warranty
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setBulkFor(b.uuid)} className="cursor-pointer gap-1 text-[11px] uppercase tracking-[0.08em] text-[var(--ink-3)] hover:text-[var(--ink)]">
            <Upload size={12} />
            Bulk
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setUnitFor(b.uuid)} className="cursor-pointer gap-1 text-[11px] uppercase tracking-[0.08em] text-[var(--green)] hover:text-[var(--ink)]">
            <Plus size={12} />
            Unit
          </Button>
        </div>
      </div>
      {b.units.length > 0 && (
        <div className="mt-1 overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)]">
          {/* Column header (Product-Library style) */}
          <div className="grid grid-cols-[28px_1.1fr_0.9fr_1fr_1.3fr_0.9fr_1fr_0.8fr] gap-2 border-b-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-1.5 text-[10px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
            <span className="flex items-center">
              {claimableUnits.length > 0 && (
                <Checkbox
                  checked={allClaimableSelected}
                  onCheckedChange={() => toggleAllClaimable(claimableUnits)}
                  aria-label="Select all units"
                  className="cursor-pointer"
                />
              )}
            </span>
            <span>Unit / Apartment / Home No</span>
            <span>Warranty Reg No</span>
            <span>Warranty Type</span>
            <span>Product</span>
            <span>Colour</span>
            <span>Contractor</span>
            <span>Registration</span>
          </div>
          {b.units.map((u) => (
            <div key={u.uuid} className="grid grid-cols-[28px_1.1fr_0.9fr_1fr_1.3fr_0.9fr_1fr_0.8fr] items-center gap-2 border-b-[0.5px] border-[var(--rule)] px-3 py-2 text-[12px] text-[var(--ink)] last:border-b-0">
              <span className="flex items-center">
                {u.registered && u.holding_uuid && (
                  <Checkbox
                    checked={selectedClaimUnits.has(u.uuid)}
                    onCheckedChange={() => toggleClaimUnit(u.uuid)}
                    aria-label={`Select ${u.name}`}
                    className="cursor-pointer"
                  />
                )}
              </span>
              <span>{u.name}</span>
              <span className="font-mono text-[11px] text-[var(--ink-2)]">
                {u.registered
                  ? u.registration_number ||
                    (u.holding_uuid ? u.holding_uuid.slice(0, 8) : '-')
                  : '-'}
              </span>
              <span>
                {u.warranty_type ? (
                  <Badge variant="outline">{u.warranty_type}</Badge>
                ) : (
                  <span className="text-[var(--ink-2)]">-</span>
                )}
              </span>
              <span className="text-[var(--ink-2)]">{u.product || '-'}</span>
              <span className="text-[var(--ink-2)]">{u.color || '-'}</span>
              <span className="text-[var(--ink-2)]">{u.contractor || '-'}</span>
              <span>
                {u.registered ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-[11px] text-[var(--green)]">
                      <Check size={12} />
                      Registered
                    </span>
                    {u.holding_uuid && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          navigate(`/claims/submit?holding=${u.holding_uuid}`)
                        }
                        className="h-auto cursor-pointer p-0 text-[11px] uppercase tracking-[0.08em] text-[var(--ink-3)] hover:text-[var(--ink)]"
                      >
                        Submit claim
                      </Button>
                    )}
                  </span>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRegisterUnit(u)}
                    className="h-auto cursor-pointer p-0 text-[11px] uppercase tracking-[0.08em] text-[var(--green)] hover:text-[var(--ink)]"
                  >
                    Register
                  </Button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
    )
  }

  const renderCommunity = (c: HoaCommunityNode, indent: boolean) => (
    <div key={c.uuid} className={indent ? 'ml-5 border-l-[0.5px] border-[var(--rule)] pl-4' : ''}>
      <div className="flex items-center justify-between py-1.5">
        <div className="flex items-center gap-2">
          <Building2 size={15} className="text-[var(--green)]" />
          <span className="text-[14px] text-[var(--ink)]">{c.name}</span>
          {c.address && (
            <span className="flex items-center gap-1 text-[11px] text-[var(--ink-3)]">
              <MapPin size={11} />
              {c.address}
            </span>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setBuildingFor(c.uuid)} className="cursor-pointer gap-1 text-[11px] uppercase tracking-[0.08em] text-[var(--green)] hover:text-[var(--ink)]">
          <Plus size={12} />
          Building
        </Button>
      </div>
      {c.buildings.map(renderBuilding)}
    </div>
  )

  // Developer chips (for adding a community under a specific developer).
  const developers = useMemo(
    () =>
      tree
        .filter((n) => n.type === 'developer')
        .map((n) => ({ uuid: n.uuid, name: n.name })),
    [tree],
  )

  // Drill-in detail for one community: its buildings/units management
  // surface plus that community's claims.
  const renderCommunityDetail = (c: CommunityRow) => {
    return (
      <div className="flex flex-col gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setSelectedClaimUnits(new Set())
            setSelectedCommunityUuid(null)
          }}
          className="w-fit cursor-pointer gap-1.5 px-0 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] hover:text-[var(--ink)]"
        >
          <ArrowLeft size={13} />
          All HOA communities
        </Button>

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
            <Briefcase size={13} className="text-[var(--ink-2)]" />
            {c.developer !== '-' ? c.developer : 'Standalone community'}
          </div>
          <div className="flex items-center gap-2">
            {bulkImportsEnabled && (
              <Button
                variant="outline"
                onClick={() => setBulkImportOpen(true)}
                className="cursor-pointer gap-1.5"
              >
                <Upload size={14} />
                Upload CSV
              </Button>
            )}
            <Button
              onClick={() => setBulkClaimsOpen(true)}
              disabled={c.registered === 0}
              className="cursor-pointer gap-1.5"
              title={
                c.registered === 0
                  ? 'Register units first to file claims'
                  : undefined
              }
            >
              <Layers size={14} />
              {gridSelectedUnits.length > 0
                ? `Bulk File Claims (${gridSelectedUnits.length})`
                : 'Bulk File Claims'}
            </Button>
          </div>
        </div>

        {/* Buildings + units management surface */}
        <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-4">
          {renderCommunity(c.node, false)}
        </div>

        {/* This community's claims */}
        <div>
          <h3 className="mb-2 text-[12px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
            Claims
          </h3>
          <HoaClaimsTable
            tableId="hoa-community-claims"
            scope={{ host_uuid: c.uuid }}
            columns={claimColumns}
            communityByHolding={communityByHolding}
            emptyMessage="No claims yet for this community."
            onRowClick={(r) => navigate(`/claims/${r.uuid}`)}
          />
        </div>
      </div>
    )
  }

  return (
    <AppShell>
      <main className="page-container py-8">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-[32px] leading-none text-[var(--ink)]">HOA</h1>
              <ConfigHelp sections={["display.hoa_table"]} returnLabel="HOA" />
            </div>
            <p className="mt-2 max-w-[640px] text-[12px] leading-[1.6] text-[var(--ink-3)]">
              Your home base for Homeowners Associations. Register homes one at a
              time or in bulk, file single or bulk claims, and settle as one
              payment to the HOA or pay each owner - all in one place.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setAddDevOpen(true)} className="cursor-pointer gap-1.5">
              <Plus size={14} />
              Developer
            </Button>
            {registrationGroupsEnabled && (
              <Button variant="outline" onClick={() => setBulkCreateOpen(true)} className="cursor-pointer gap-1.5">
                <Upload size={14} />
                Bulk Register HOA
              </Button>
            )}
            <Button onClick={() => navigate('/hoa-communities/new')} className="cursor-pointer gap-1.5">
              <Plus size={14} />
              Add New HOA
            </Button>
          </div>
        </div>

        {isLoading ? (
          <p className="py-12 text-center text-[13px] text-[var(--ink-3)]">Loading…</p>
        ) : !hasContent ? (
          <div className="flex flex-col items-center justify-center rounded-[8px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-6 py-16 text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--bg)]">
              <Building2 size={22} className="text-[var(--ink-3)]" />
            </div>
            <p className="mb-1 text-[15px] text-[var(--ink)]">Nothing yet</p>
            <p className="mb-5 max-w-[440px] text-[12px] leading-[1.6] text-[var(--ink-3)]">
              Add a master developer (e.g. Emaar), register its communities, then add buildings and units (one at a time or bulk upload).
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setAddDevOpen(true)} className="cursor-pointer gap-1.5">
                <Plus size={14} />
                Add Developer
              </Button>
              <Button onClick={() => navigate('/hoa-communities/new')} className="cursor-pointer gap-1.5">
                <Plus size={14} />
                Add New HOA
              </Button>
            </div>
          </div>
        ) : selectedCommunity ? (
          renderCommunityDetail(selectedCommunity)
        ) : (
          <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
            <TabsList>
              <TabsTrigger value="communities">
                Communities ({communities.length})
              </TabsTrigger>
              <TabsTrigger value="registrations">
                Registrations ({unitRows.length})
              </TabsTrigger>
              <TabsTrigger value="claims">Claims ({totalClaims})</TabsTrigger>
            </TabsList>

            <TabsContent value="communities" className="mt-4">
              {developers.length > 0 && (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
                    Add community under
                  </span>
                  {developers.map((d) => (
                    <Button
                      key={d.uuid}
                      variant="outline"
                      size="xs"
                      onClick={() =>
                        navigate(`/hoa-communities/new?developer=${d.uuid}`)
                      }
                      className="cursor-pointer gap-1"
                    >
                      <Plus size={11} />
                      {d.name}
                    </Button>
                  ))}
                </div>
              )}
              <TreeTable
                tableId="hoa-communities-overview"
                rows={communities}
                columns={communityColumns}
                facets={[
                  valuesFacet(
                    'developer',
                    'Developer',
                    communities.map((c) => c.developer),
                    (d) => (d === '-' ? 'Standalone community' : d),
                  ),
                ]}
                matches={communityMatches}
                searchPlaceholder="Search communities..."
                rowKey={(r) => r.uuid}
                onRowClick={(r) => setSelectedCommunityUuid(r.uuid)}
              />
            </TabsContent>

            <TabsContent value="registrations" className="mt-4">
              <TreeTable
                tableId="hoa-registrations"
                rows={unitRows}
                columns={unitColumns}
                facets={[
                  valuesFacet('community', 'Community', unitRows.map((r) => r.community)),
                  valuesFacet(
                    'status',
                    'Status',
                    unitRows.map((r) => (r.registered ? 'registered' : 'pending')),
                    (v) => (v === 'registered' ? 'Registered' : 'Pending'),
                  ),
                  valuesFacet('warranty_type', 'Warranty Type', unitRows.map((r) => r.warrantyType)),
                ]}
                matches={unitMatches}
                searchPlaceholder="Search homes..."
                rowKey={(r) => r.uuid}
                onRowClick={(r) =>
                  r.registered && r.holding_uuid
                    ? navigate(`/registrations/${r.holding_uuid}`)
                    : undefined
                }
              />
            </TabsContent>

            <TabsContent value="claims" className="mt-4">
              <HoaClaimsTable
                tableId="hoa-claims"
                scope={ALL_COMMUNITIES}
                columns={claimColumns}
                communityByHolding={communityByHolding}
                emptyMessage="No HOA claims yet."
                onRowClick={(r) => navigate(`/claims/${r.uuid}`)}
              />
            </TabsContent>
          </Tabs>
        )}
      </main>

      <QuickAddDialog
        open={addDevOpen}
        onOpenChange={setAddDevOpen}
        title="Add Master Developer"
        description="A master builder (e.g. Emaar) that owns multiple communities."
        fieldLabel="Developer name"
        placeholder="e.g. Emaar"
        pending={createDeveloper.isPending}
        onSubmit={async (name) => {
          try {
            await createDeveloper.mutateAsync({ name })
            toast.success(`Developer "${name}" added`)
            setAddDevOpen(false)
          } catch (err) {
            toastApiError(err, 'Failed to add developer')
          }
        }}
      />

      <QuickAddDialog
        open={!!buildingFor}
        onOpenChange={(o) => !o && setBuildingFor(null)}
        title="Add Building / Block"
        description="A named building within the community (e.g. Beach Vista Tower 1)."
        fieldLabel="Building name"
        placeholder="e.g. Beach Vista Tower 1"
        pending={createBuilding.isPending}
        onSubmit={async (name) => {
          if (!buildingFor) return
          try {
            await createBuilding.mutateAsync({ community_uuid: buildingFor, name })
            toast.success(`Building "${name}" added`)
            setBuildingFor(null)
          } catch (err) {
            toastApiError(err, 'Failed to add building')
          }
        }}
      />

      <UnitDialog
        open={!!unitFor}
        onOpenChange={(o) => !o && setUnitFor(null)}
        pending={createUnit.isPending}
        onSubmit={async (v) => {
          if (!unitFor) return
          try {
            await createUnit.mutateAsync({ building_uuid: unitFor, ...v })
            toast.success(`Unit "${v.label}" added`)
            setUnitFor(null)
          } catch (err) {
            toastApiError(err, 'Failed to add unit')
          }
        }}
      />

      <BulkUnitsDialog
        open={!!bulkFor}
        onOpenChange={(o) => !o && setBulkFor(null)}
        pending={bulkUnits.isPending}
        onSubmit={async (rows: BulkRegisterRow[]) => {
          if (!bulkFor) return
          try {
            const res = await bulkUnits.mutateAsync({ building_uuid: bulkFor, rows })
            toast.success(
              `${res.created} unit${res.created !== 1 ? 's' : ''} added` +
                (res.registered ? `, ${res.registered} registered` : '') +
                (res.skipped ? ` (${res.skipped} skipped)` : ''),
            )
            setBulkFor(null)
          } catch (err) {
            toastApiError(err, 'Failed to bulk add units')
          }
        }}
      />

      <BuildingWarrantyDialog
        building={warrantyForBuilding}
        onOpenChange={(o) => !o && setWarrantyForBuilding(null)}
        pending={setBuildingWarranty.isPending}
        onSubmit={async (optionUuid) => {
          if (!warrantyForBuilding) return
          try {
            const res = await setBuildingWarranty.mutateAsync({
              building_uuid: warrantyForBuilding.uuid,
              warranty_template_option_uuid: optionUuid,
            })
            toast.success(`Default warranty set: ${res.default_warranty_name}`)
            setWarrantyForBuilding(null)
          } catch (err) {
            toastApiError(err, 'Failed to set warranty')
          }
        }}
      />

      <RegisterUnitDialog
        unit={registerUnit}
        onOpenChange={(o) => !o && setRegisterUnit(null)}
        pending={registerUnitMut.isPending}
        onSubmit={async (v) => {
          if (!registerUnit) return
          try {
            await registerUnitMut.mutateAsync({ unit_uuid: registerUnit.uuid, ...v })
            toast.success(`${registerUnit.name} registered`)
            setRegisterUnit(null)
          } catch (err) {
            toastApiError(err, 'Failed to register unit')
          }
        }}
      />

      {selectedCommunity && (
        <BulkFileClaimsDialog
          open={bulkClaimsOpen}
          onOpenChange={setBulkClaimsOpen}
          communityUuid={selectedCommunity.uuid}
          communityName={selectedCommunity.name}
          units={claimDialogUnits}
        />
      )}
      {bulkImportsEnabled && (
        <BulkImportDialog
          open={bulkImportOpen}
          onOpenChange={setBulkImportOpen}
          target="claim:hoa"
          title="Bulk Upload Claims"
          onComplete={() => {
            void queryClient.invalidateQueries({ queryKey: ['claims'] })
            void refetchTree()
          }}
        />
      )}
      {registrationGroupsEnabled && (
        <BulkImportDialog
          open={bulkCreateOpen}
          onOpenChange={setBulkCreateOpen}
          target="registration_group"
          title="Bulk Register Community"
          onComplete={() => void refetchTree()}
        />
      )}
    </AppShell>
  )
}
